import type { SelectChangeEvent } from '@mui/material';

import React from 'react';

import {
  Box,
  Select,
  Button,
  Popover,
  MenuItem,
  useTheme,
  TextField,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { useTranslate } from 'src/locales';

import { Iconify } from '../iconify';

interface Filter {
  column: string;
  operator: string;
  value: string;
}

interface FilterPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  advanceFilterFields?: any;
  setAdvanceFilter?: any;
  advanceFilters?: any;
  resetPagination?: any;
}

const operators = [
  'contains',
  'does not contain',
  'equals',
  'does not equal',
  'starts with',
  'ends with',
  'is empty',
  'is not empty',
];

const FilterPopover: React.FC<FilterPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  advanceFilterFields,
  setAdvanceFilter,
  advanceFilters,
  resetPagination,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const defaultColumn = advanceFilterFields[0];
  const { t } = useTranslate('translation');

  const [filters, setFilters] = React.useState<Filter[]>([
    { column: defaultColumn, operator: 'contains', value: '' },
  ]);

  const updateFilter = (index: number, key: keyof Filter, value: string) => {
    const updated = [...filters];
    updated[index][key] = value;
    setFilters(updated);
  };

  const addFilter = () => {
    setFilters([...filters, { column: defaultColumn, operator: 'contains', value: '' }]);
  };

  const removeFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const removeAll = () => {
    setFilters([]);
  };

  const handleAdvanceFilterSubmit = () => {
    const updatedFilters = filters.map((filter) => {
      if (filter.column === 'Phone' && filter?.value && filter?.value.startsWith('0')) {
        return {
          ...filter,
          value: '966' + filter.value.slice(1),
        };
      }
      return filter;
    });
    setAdvanceFilter(updatedFilters);
    onClose();
    resetPagination();
  };

  React.useEffect(() => {
    if (open) {
      setFilters(
        advanceFilters && advanceFilters.length > 0
          ? advanceFilters
          : [{ column: defaultColumn, operator: 'contains', value: '' }]
      );
    }
  }, [open, advanceFilters, defaultColumn]);

  const isFieldWithOutValue = ['is empty', 'is not empty'];

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Box
        p={2}
        width={isMobile ? 300 : 600}
        sx={{
          maxHeight: isMobile ? '70vh' : 'auto', // for mobile, limit height to 70% of viewport height
          overflowY: isMobile ? 'auto' : 'visible', // enable vertical scrolling only on mobile
        }}
      >
        {filters.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={60} mb={1}>
            <Typography variant="body2" color="text.secondary">
              {t('ADD_FILTER')}
            </Typography>
          </Box>
        ) : (
          filters.map((filter, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                mb: 2,
                ...(isMobile
                  ? {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      p: 2,
                    }
                  : {
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1,
                      alignItems: 'center',
                    }),
              }}
            >
              {/* Mobile remove icon */}
              {isMobile && (
                <IconButton
                  onClick={() => removeFilter(index)}
                  size="small"
                  disabled={filters.length < 2}
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -5,
                    zIndex: 1,
                  }}
                >
                  <Iconify icon="mdi:close" />
                </IconButton>
              )}

              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: isMobile ? 'flex-start' : 'space-evenly',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 1.5 : 1,
                }}
              >
                <Select
                  sx={{ width: isMobile ? '100%' : '28%' }}
                  value={filter.column}
                  onChange={(e: SelectChangeEvent<string>) =>
                    updateFilter(index, 'column', e.target.value)
                  }
                  size="small"
                >
                  {advanceFilterFields.map((col: any) => (
                    <MenuItem key={col} value={col}>
                      {t(col.toUpperCase().replace(/ /g, '_'))}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  sx={{ width: isMobile ? '100%' : '28%' }}
                  value={filter.operator}
                  onChange={(e: SelectChangeEvent<string>) =>
                    updateFilter(index, 'operator', e.target.value)
                  }
                  size="small"
                >
                  {operators.map((op) => (
                    <MenuItem
                      key={op}
                      value={op}
                      disabled={filter.column === 'Label name' && isFieldWithOutValue.includes(op)}
                    >
                      {t(op.toUpperCase().replace(/ /g, '_'))}
                    </MenuItem>
                  ))}
                </Select>

                {!isFieldWithOutValue.includes(filter.operator) && (
                  <TextField
                    value={filter.value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateFilter(index, 'value', e.target.value.trim())
                    }
                    size="small"
                    placeholder={t('FILTER_VALUE')}
                    sx={{ width: isMobile ? '100%' : '28%' }}
                  />
                )}
              </Box>

              {/* Desktop remove icon */}
              {!isMobile && (
                <IconButton
                  onClick={() => removeFilter(index)}
                  size="small"
                  disabled={filters.length < 2}
                >
                  <Iconify icon="mdi:close" />
                </IconButton>
              )}
            </Box>
          ))
        )}

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Box>
            <Button onClick={addFilter}>
              <Iconify icon="mdi:filter-plus" />
              {t('ADD_FILTER')}
            </Button>
            <Button onClick={removeAll}>
              <Iconify icon="mdi:filter-off" />
              {t('REMOVE_ALL')}
            </Button>
          </Box>
          <Button color="error" onClick={handleAdvanceFilterSubmit} type="submit">
            {t('APPLY')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default FilterPopover;
